import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Dispatch, SetStateAction, useEffect, useMemo, useRef, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import CrioAlertDialog from '@crio/crio-react-component/dist/cjs/components/Feedback/CrioAlertDialog';
import { DataPointSourceType } from '@crio/common-js-esource';

import {
  DataPoint, InitialAlertContent, InstructionContent, ProcedureInterface, ProcedureStatusInterface, SubjectData, VisitInterface,
} from '../../types';
import {
  CarryForwardType, isAnyProcedureMode, isAnyReadOnlyMode, ProcedureDisplayType, ProcedureStatus, VisitModeType,
} from '../../enums';
import { getEmptyPromise } from '../../util/promiseUtil';
import { hasReferenceQuestions } from '../../util/procedureUtils';
import { Column, Row } from '..';
import Grid from './Grid';
import InstructionText from './InstructionText';
import spinner from '../../assets/spinner.gif';
import MultiRecord from './MultiRecord';
import NormalProcedure from './NormalProcedure';
import { ProcedureContextProvider } from '../../context/ProcedureContext';
import Slideout from '../Visit/Slideout';
import ReadOnlyModeSvg from '../../assets/read_only_mode.svg';
import SandboxModeSvg from '../../assets/sandbox_mode.svg';
import { ProgressNoteContextProvider } from '../../context/ProgressNoteContext';
import EconsentTooltip from './EconsentTooltip';
import { EsourceInfoDialog } from './Dialog';
import { SaveProcedureAlertsDialog, SaveProcedureAlertsDialogProps } from './Dialog/SaveProcedure';
import AlertDropdown from './AlertDropdown';
import { SaveAlertContextProvider } from '../../context/SaveAlertContext';
import {
  fetchDataPoints, fetchReferenceDataPoints, fetchSubjectVisitConfig,
} from '../../api/esourceService';
import useProcedureLock from '../../hooks/useProcedureLock';
import RulesPreview from './RulesPreview';
import RequiresTranscriptionToggle from './RequiresTranscriptionToggle';
import EmptyProcedurePreviewPage from './EmptyProcedurePreview';

const ProcedureRow = styled(Row)`
  height: calc(100vh - 39px);
`;
interface ProcedureNameContainerProps {
  showBottomBorder: boolean,
}
const ProcedureName = styled(Row) <ProcedureNameContainerProps>`
  font-size: 22px;
  font-weight: 500;
  font-family: "Poppins-Medium", "Poppins Medium", "Poppins", sans-serif;
  border-bottom: ${(props) => (props.showBottomBorder ? (`1px dotted${props.theme.palette.grey[500]}`) : 'none')};
`;
const ProcedureComponent = styled(Column)`
  width: 100%;
  overflow-x: scroll;

  .Link {
    color: ${(props) => props.theme.palette.linkText.main};
    cursor: pointer;
    &:hover {
      color: ${(props) => props.theme.palette.linkText.dark};
      text-decoration: underline;
    }
  }

  button {
    font-weight: 500;
  }

  div.Openable {
    .RecordHeader {
      cursor: pointer;
      justify-content: space-between;
    }
    .Collapsible {
      height: auto;
    }

    &.Closed {

      .Collapsible {
        height: 0;
        overflow: hidden;
      }
    }
  }

  div.Closed {
    .Hideable {
      display: none;
    }
  }

  .SyntheticQuestionCheckbox {
    padding: 20px 5px;
  }

  .Grid, .MultiRecord {
    padding-top: 30px;
  }

  .MultiRecordBtns {
    margin-right: 3%;
    margin-top: 15px;
  }
`;

const StyledSpinner = styled('img')`
  text-align: center;
  margin: auto;
`;

const StyledProcedureHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 22px;
`;

const renderProcedureComponent = (
  {
    visitConfig,
    procedureId: currentProcedureId,
    saveProcedureAlertsDialogValues,
    setSaveProcedureAlertsDialogValues,
    initialAlert,
    setInitialAlert,
    subjectData,
  }: RenderProcedureProps,
) => {
  const { timeZone, isEdc } = subjectData;

  const procedure = visitConfig.procedures.find(({ procedureId }) => procedureId === currentProcedureId)!;
  procedure.versionId = visitConfig.versionId;
  const { displayType, questions } = procedure;

  const procedureComponent = useMemo(() => {
    switch (displayType) {
      case ProcedureDisplayType.MULTI_RECORD:
        return <MultiRecord timeZone={timeZone} isEdc={isEdc} />;
      case ProcedureDisplayType.GRID:
        return (
          <Grid
            {...procedure}
            options={questions[0].answerOptions!.map((answerOption) => answerOption.text)}
          />
        );
      default:
        return (
          <NormalProcedure {...procedure} />
        );
    }
  }, [currentProcedureId, visitConfig]);

  return (
    <>
      {saveProcedureAlertsDialogValues.isOpen && (
        <SaveAlertContextProvider>
          <SaveProcedureAlertsDialog
            {...saveProcedureAlertsDialogValues}
            closeHandler={() => {
              setSaveProcedureAlertsDialogValues((prevState) => ({ ...prevState, isOpen: false }));
            }}
          />
        </SaveAlertContextProvider>
      )}
      {initialAlert
        && (
          <EsourceInfoDialog
            open={initialAlert !== undefined}
            closeHandler={() => setInitialAlert(undefined)}
            alertTitle={initialAlert.header || ''}
          >
            <p>{initialAlert.body}</p>
            {initialAlert?.bodyItems
              && (
                <ul>
                  {initialAlert?.bodyItems?.map((bodyItem: string) => (<li key={bodyItem}>{bodyItem}</li>))}
                </ul>
              )}
          </EsourceInfoDialog>
        )}
      {procedureComponent}
    </>
  );
};

export interface RenderProcedureProps extends ProcedureTabProps {
  saveProcedureAlertsDialogValues: SaveProcedureAlertsDialogProps,
  setSaveProcedureAlertsDialogValues: Dispatch<SetStateAction<SaveProcedureAlertsDialogProps>>,
  initialAlert: InitialAlertContent | undefined,
  setInitialAlert: Dispatch<SetStateAction<InitialAlertContent | undefined>>,
  subjectData: SubjectData,
}

export interface ProcedureTabProps {
  nextProcedureId?: string,
  initialProcedureStatus?: ProcedureStatus,
  procedureId: string,
  subjectData: SubjectData,
  visitConfig: VisitInterface,
  visitMode: VisitModeType,
  setHasUnsavedChanges: (newVal: boolean) => void,
  setProcedureId: Dispatch<SetStateAction<string | null>>,
  setProcedureStatuses: Dispatch<SetStateAction<ProcedureStatusInterface[]>>,
  setShowCompleteVisitPage: Dispatch<SetStateAction<boolean>>,
  setVisitConfig: Dispatch<SetStateAction<VisitInterface | undefined>>,
  subjectHeaderHeight: number,
}

interface FetchVisitConfigProps {
  visitMode: VisitModeType | undefined,
  procedureId: string,
  studyId: string,
  subjectId?: string,
  visitId?: string,
  procedureTemplateId?: string,
  setProcedureId: Dispatch<SetStateAction<string | null>>,
}
/**
 * Fetch the most up to date visit config.  If the component becomes unmounted while we're asyncronously
 * fetching things from the server, do not attempt to update state.
 * @param FetchVisitConfigProps
 * @returns The visit config necessary to render this procedure
 */
export async function _doFetchVisitConfig({ // Only exported for testing purposes
  visitMode, procedureId, studyId, subjectId, visitId, procedureTemplateId, setProcedureId,
}: FetchVisitConfigProps): Promise<VisitInterface> {
  const newVisitConfig: VisitInterface = await fetchSubjectVisitConfig(visitMode, studyId!, subjectId, visitId, procedureId, procedureTemplateId);

  const { procedures: newProcedures } = newVisitConfig || {};
  const newProcedureConfig = newProcedures?.find(({ procedureId: pId }) => procedureId === pId);
  if (!newProcedureConfig) {
    // We need to be careful - if the selected procedure no longer exists in the config,
    // default to the first procedure (this is the same as the old siteapp behavior).
    const firstProcedureId = newProcedures?.[0]?.procedureId;
    if (firstProcedureId) {
      setProcedureId(firstProcedureId);
    } else {
      throw new Error('Could not find any procedures in new config that was supplied');
    }
  }

  return newVisitConfig;
}

interface FetchDataPointsProps {
  procedureId: string,
  procedureReadOnly: boolean,
  studyId: string,
  subjectId?: string,
  visitId?: string,
  visitMode: VisitModeType,
}
/**
 * Simple routine to just fetch all the datapoints that exist on the currently specified procedure. If the
 * component becomes unmounted while we're asyncronously fetching things from the server, do not attempt to
 * update state.
 * @param FetchDataPointsProps
 * @returns The datapoints for this procedure
 */
export async function _doFetchDataPoints({ // Only exported for testing purposes
  procedureId, studyId, subjectId, visitId, visitMode,
}: FetchDataPointsProps): Promise<Array<DataPoint>> {
  const fetchedDataPoints = await fetchDataPoints({
    visitMode,
    studyId: studyId!,
    subjectId,
    visitId,
    procedureId,
  });

  return fetchedDataPoints;
}

/**
 * Simple routine to just fetch all the reference datapoints that exist on the currently specified procedure. If the
 * component becomes unmounted while we're asyncronously fetching things from the server, do not attempt to
 * update state.
 * @param FetchDataPointsProps
 * @returns The datapoints for this procedure
 */
// TODO: Only fetch if the configuration has a reference question type in it
/// https://clinical-research-io.atlassian.net/browse/ES-668
export async function _doFetchReferenceDataPoints({ // Only exported for testing purposes
  procedureId, studyId, subjectId, visitId, visitMode,
}: FetchDataPointsProps): Promise<Array<DataPoint>> {
  return fetchReferenceDataPoints({
    visitMode,
    studyId: studyId!,
    subjectId,
    visitId,
    procedureId,
  });
}

export const getInstructionsWithPrepopulatedDataMessage = (
  instructions: InstructionContent | undefined,
  dataPoints: DataPoint[],
  traitsDataMessage: string,
  apiDataMessage: string,
): InstructionContent => {
  const prefilledFromTraits = dataPoints.some((dp) => dp.source_id === DataPointSourceType.RECRUITMENT_TRAITS);
  const prefilledFromApi = dataPoints.some((dp) => dp.source_id === DataPointSourceType.ESOURCE_API);
  const { bodyItems: originalBodyItems = [] } = instructions || {};
  if (prefilledFromTraits || prefilledFromApi) {
    const message = prefilledFromApi ? apiDataMessage : traitsDataMessage;
    return {
      ...(instructions || {}),
      bodyItems: [...originalBodyItems, message],
    };
  }
  return instructions || {};
};

interface LoadingSpinnerProps {
  isLoading: boolean;
}

function LoadingSpinner({ isLoading }: LoadingSpinnerProps): JSX.Element | null {
  return isLoading ? (
    <StyledSpinner
      data-testid="loadingSpinner"
      alt="Loading…"
      src={spinner}
      height="42px"
      width="42px"
    />
  ) : null;
}

interface ProcedureWrapperProps {
  isLoading: boolean;
  isRunningInitialRules: boolean;
  instructionsHeader: JSX.Element;
  readOnlyMode: boolean;
  subjectData: SubjectData
  visitMode: VisitModeType;
  setSaveProcedureAlertsDialogValues: Dispatch<SetStateAction<SaveProcedureAlertsDialogProps>>;
  saveProcedureAlertsDialogValues: SaveProcedureAlertsDialogProps;
  initialAlert?: InitialAlertContent;
  setInitialAlert: Dispatch<SetStateAction<InitialAlertContent | undefined>>;
  procedure: ProcedureInterface;
  procedureTabProps: ProcedureTabProps;
}

function ProcedureWrapper(props: ProcedureWrapperProps) {
  const {
    isRunningInitialRules, isLoading, instructionsHeader, readOnlyMode, subjectData,
    visitMode, setSaveProcedureAlertsDialogValues, saveProcedureAlertsDialogValues, initialAlert, setInitialAlert, procedure, procedureTabProps,
  } = props;
  const {
    allowSkip, carryForwardType, instructions, name, status, rules,
  } = procedure;
  const isProcedureLoading: boolean = (isRunningInitialRules || isLoading)!!;

  if (isProcedureLoading) {
    return <LoadingSpinner isLoading={isProcedureLoading} />;
  }
  return (
    <ProcedureComponent className="CenterPagePadding">
      <StyledProcedureHeader>
        <ProcedureName showBottomBorder={!instructions}>{name}</ProcedureName>
        { subjectData.isEdc && carryForwardType !== CarryForwardType.PERMANENT && !isAnyProcedureMode(visitMode) && (<RequiresTranscriptionToggle isDisabled={readOnlyMode} />)}
      </StyledProcedureHeader>
      <InstructionText instructions={instructions!} header={instructionsHeader} allowSkip={allowSkip} procedureStatus={status} readOnly={readOnlyMode} truncate={false} />
      <AlertDropdown />
      {renderProcedureComponent({
        ...procedureTabProps, setSaveProcedureAlertsDialogValues, saveProcedureAlertsDialogValues, initialAlert, setInitialAlert, subjectData,
      })}
      {visitMode === VisitModeType.PROCEDURE_PREVIEW
            && <RulesPreview rules={rules} />}
    </ProcedureComponent>
  );
}
/**
 * Generic Procedure component that handles rendering the different procedure types.
 * @returns
 */
export default function Procedure(props: ProcedureTabProps) {
  const {
    nextProcedureId,
    procedureId: currentProcedureId,
    initialProcedureStatus = ProcedureStatus.NOT_STARTED,
    setHasUnsavedChanges,
    setProcedureId,
    setProcedureStatuses,
    setShowCompleteVisitPage,
    setVisitConfig,
    subjectData,
    visitConfig,
    visitMode,
    subjectHeaderHeight,
  } = props;
  const mountedRef = useRef(true);
  const { subjectId } = subjectData;

  const procedure = visitConfig.procedures.find(({ procedureId: pId }) => pId === currentProcedureId)!;
  const [saveProcedureAlertsDialogValues, setSaveProcedureAlertsDialogValues] = useState<SaveProcedureAlertsDialogProps>({
    isOpen: false, deletingRecord: false, missingAnswers: [], changedAnswers: [], nothingEverAnswered: true, currentProcedure: procedure,
  });
  const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
  const [referenceDataPoints, setReferenceDataPoints] = useState<DataPoint[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRunningInitialRules, setIsRunningInitialRules] = useState<boolean>(true);
  const [initialAlert, setInitialAlert] = useState<InitialAlertContent | undefined>(undefined);
  const {
    carryForwardType, hasEconsent, name, readOnly: procedureReadOnly, procedureLocked, initialAlertContent, questions,
  } = procedure;
  const { t } = useTranslation();
  const { studyId, visitId, procedureTemplateId } = useParams();

  const [showLockAlert, setShowLockAlert] = useState(true);
  const {
    lockHolder, fetchInitialLock, handleLockResult, releaseLock,
  } = useProcedureLock({
    procedureId: currentProcedureId,
    procedureReadOnly,
    studyId: studyId!,
    subjectId,
    visitId,
    carryForwardType,
    isLoading,
    setVisitConfig,
  });
  useEffect(() => { setShowLockAlert(true); }, [lockHolder]);
  const lockedAlert = lockHolder && showLockAlert ? t('Procedure.Dialog.Locked.Message', { userName: lockHolder }) : '';
  const readOnlyMode = isAnyReadOnlyMode(visitMode) || procedureReadOnly || !!procedureLocked;
  const traitsDataMessage = t('Procedure.Traits Data Message');
  const apiDataMessage = t('Procedure.API Data Message');

  if (!questions || questions.length === 0) {
    return <EmptyProcedurePreviewPage />;
  }

  const instructionsHeader = (
    <Row className="InstructionsHeader">
      <h3>{t('Procedure.Instructions')}</h3>
      {visitMode === VisitModeType.DEFAULT && hasEconsent && <EconsentTooltip disabled={readOnlyMode} subjectData={subjectData} visitConfig={visitConfig} />}
    </Row>
  );

  useEffect(() => {
    const initializeProcedure = async (): Promise<void> => {
      const dataPointsPromise = _doFetchDataPoints({
        procedureId: currentProcedureId,
        procedureReadOnly,
        studyId: studyId!,
        subjectId,
        visitId,
        visitMode,
      });

      const referenceDataPointsPromise: Promise<DataPoint[]> = hasReferenceQuestions(procedure)
        ? _doFetchReferenceDataPoints({
          procedureId: currentProcedureId,
          procedureReadOnly,
          studyId: studyId!,
          subjectId,
          visitId,
          visitMode,
        })
        : getEmptyPromise([]);

      const lockPromise = fetchInitialLock();
      const visitConfigPromise = _doFetchVisitConfig({
        visitMode,
        procedureId: currentProcedureId,
        studyId: studyId!,
        subjectId,
        visitId,
        procedureTemplateId,
        setProcedureId,
      });

      const [fetchedDataPoints, fetchedReferenceDataPoints, fetchedLock, fetchedVisitConfig] = await Promise.all([dataPointsPromise, referenceDataPointsPromise, lockPromise, visitConfigPromise]);

      if (!mountedRef.current) return;

      // fetchedVisitConfig set in handleLockResult because lock result may impact readonly status in visit config which impacts what we render
      handleLockResult(fetchedLock, fetchedVisitConfig);
      setDataPoints(fetchedDataPoints);
      setReferenceDataPoints(fetchedReferenceDataPoints);
      setIsLoading(false);
    };

    initializeProcedure();
  }, [currentProcedureId]);

  useEffect(() => {
    // Keep the initial alert content up to date
    setInitialAlert(initialAlertContent);
  }, [isLoading]);

  // This useEffect is to update our ref on unmount which we later use to determine
  // if we should ignore the results of long-running asynchronous tasks
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const instructionsWithPrepopulatedDataMessage = useMemo(() => getInstructionsWithPrepopulatedDataMessage(procedure.instructions, dataPoints, traitsDataMessage, apiDataMessage), [dataPoints, procedure.instructions]);

  return (
    <>
      {lockedAlert && (
        <CrioAlertDialog
          type="Info"
          open={!!lockedAlert}
          onClose={() => {
            setShowLockAlert(false);
          }}
          fullWidth={false}
          disablePortal
          sx={{ width: '50%', margin: 'auto' }}
        >
          <div data-testid="lockErrorMessage">{lockedAlert}</div>
        </CrioAlertDialog>
      )}
      {!isAnyProcedureMode(visitMode) && (visitMode === VisitModeType.SANDBOX || readOnlyMode) && (
        <Row className={`VisitModeBar ${readOnlyMode ? 'ReadOnly' : 'Sandbox'}`}>
          <Row className="VisitMode">
            <img
              src={readOnlyMode ? ReadOnlyModeSvg : SandboxModeSvg}
              alt="Mode"
            />
            <div>{readOnlyMode ? t('Procedure.Mode.READ-ONLY') : t('Procedure.Mode.SANDBOX')}</div>
          </Row>
        </Row>
      )}
      <ProcedureRow className="ProcedureContent CenterPageBackground">
        <ProgressNoteContextProvider
          procedureId={currentProcedureId}
          visitMode={visitMode}
          subjectData={subjectData}
          visitConfig={visitConfig}
          procedureName={procedure.name}
        >
          <ProcedureContextProvider
            isLoading={isLoading}
            dataPoints={dataPoints}
            referenceDataPoints={referenceDataPoints}
            initialProcedureStatus={initialProcedureStatus}
            nextProcedureId={nextProcedureId}
            procedureId={currentProcedureId}
            readOnly={readOnlyMode}
            setProcedureId={setProcedureId}
            setHasUnsavedChanges={setHasUnsavedChanges}
            setShowCompleteVisitPage={setShowCompleteVisitPage}
            subjectData={subjectData}
            setProcedureStatuses={setProcedureStatuses}
            setSaveProcedureAlertsDialogValues={setSaveProcedureAlertsDialogValues}
            visitConfig={visitConfig}
            visitMode={visitMode}
            visitType={visitConfig.type}
            isRunningInitialRules={isRunningInitialRules}
            setIsRunningInitialRules={setIsRunningInitialRules}
            releaseLock={releaseLock}
          >
            <ProcedureWrapper
              isRunningInitialRules={isRunningInitialRules}
              isLoading={isLoading}
              instructionsHeader={instructionsHeader}
              readOnlyMode={readOnlyMode}
              subjectData={subjectData}
              visitMode={visitMode}
              setSaveProcedureAlertsDialogValues={setSaveProcedureAlertsDialogValues}
              saveProcedureAlertsDialogValues={saveProcedureAlertsDialogValues}
              initialAlert={initialAlert}
              setInitialAlert={setInitialAlert}
              procedure={{ ...procedure, instructions: instructionsWithPrepopulatedDataMessage }}
              procedureTabProps={props}
            />
            {!(VisitModeType.SUBJECT === visitMode) && !isAnyProcedureMode(visitMode) && (
              <Slideout
                procedureName={name}
                isVisitLevel={false}
                subjectHeaderHeight={subjectHeaderHeight}
                timeZone={subjectData.timeZone}
              />
            )}
          </ProcedureContextProvider>
        </ProgressNoteContextProvider>
      </ProcedureRow>
    </>
  );
}

ProcedureWrapper.defaultProps = {
  initialAlert: undefined,
};

Procedure.defaultProps = {
  nextProcedureId: null,
  initialProcedureStatus: [],
};
